"use client";

/**
 * useAllocations Hook
 *
 * Manages strategy allocations for the authenticated user.
 * Uses session cookie for auth; wallet address is not required.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { useStrategiesStore } from "@/stores/strategiesStore";
import type { StrategyAllocation } from "@/types/api.types";

// Stale time: 10 seconds (allocations update frequently)
const STALE_TIME = 10 * 1000;

export interface AllocateParams {
  strategyId: string;
  amount: number;
  venue?: string;
  /**
   * SFWL-017: caller-supplied CFTC §1.55(b) risk-disclosure acknowledgement
   * state. Required — no default; previously hardcoded to `true` here,
   * which silently attested acknowledgement the caller never obtained.
   */
  riskAcknowledged: boolean;
}

export interface DeallocateParams {
  allocationId: string;
  amount?: number;
}

export interface UseAllocationsReturn {
  /** List of user's allocations */
  allocations: StrategyAllocation[];
  /** Loading state for allocations list */
  isLoading: boolean;
  /** Whether allocations are being refetched */
  isFetching: boolean;
  /** Allocate function */
  allocate: (params: AllocateParams) => Promise<void>;
  /** Deallocate function */
  deallocate: (params: DeallocateParams) => Promise<void>;
  /** Whether an allocation is in progress */
  isAllocating: boolean;
  /** Whether a deallocation is in progress */
  isDeallocating: boolean;
  /** Current mutation error (if any) */
  error: Error | null;
  /** Refetch allocations */
  refetch: () => void;
}

export function useAllocations(): UseAllocationsReturn {
  const { isAuthenticated, session } = useAuth();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();

  // Store actions for updating allocation state
  const setUserAllocations = useStrategiesStore((state) => state.setUserAllocations);
  const setAllocating = useStrategiesStore((state) => state.setAllocating);

  // ============================================================================
  // Query: Fetch Allocations
  // ============================================================================

  const allocationsQuery = useQuery({
    queryKey: ["allocations", userId],
    queryFn: async () => {
      // Session cookie handles auth — no wallet address needed
      const data = await api.allocations.list();

      // Update store with allocations
      const mappedAllocations = data.allocations.map((a) => ({
        id: a.id,
        strategyId: a.strategyId,
        strategyName: a.strategyName,
        allocatedAmount: a.allocatedAmount,
        currentValue: a.currentValue,
        unrealizedPnl: a.unrealizedPnl,
        unrealizedPnlPercent: a.unrealizedPnlPercent,
        realizedPnl: a.realizedPnl,
        allocatedAt: new Date(a.allocatedAt),
      }));

      setUserAllocations(mappedAllocations);
      return data;
    },
    staleTime: STALE_TIME,
    enabled: isAuthenticated,
  });

  // ============================================================================
  // Mutation: Allocate
  // ============================================================================

  const allocateMutation = useMutation({
    mutationFn: async (params: AllocateParams) => {
      if (!isAuthenticated) {
        throw new Error("Not authenticated");
      }

      setAllocating(true);

      try {
        // Session cookie handles auth — backend injects userId from session
        const response = await api.allocations.create({
          strategyId: params.strategyId,
          amount: params.amount,
          venue: params.venue ?? "demo",
          riskAcknowledged: params.riskAcknowledged,
        });

        return response;
      } finally {
        setAllocating(false);
      }
    },
    onSuccess: () => {
      // Invalidate related queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ["allocations", userId] });
      queryClient.invalidateQueries({ queryKey: ["strategies"] });
      queryClient.invalidateQueries({ queryKey: ["portfolio", userId] });
    },
  });

  // ============================================================================
  // Mutation: Deallocate
  // ============================================================================

  const deallocateMutation = useMutation({
    mutationFn: async (params: DeallocateParams) => {
      if (!isAuthenticated) {
        throw new Error("Not authenticated");
      }

      // Session cookie handles auth — no wallet address needed
      const response = await api.allocations.deallocate(params.allocationId, {
        allocationId: params.allocationId,
        amount: params.amount,
      });

      return response;
    },
    onSuccess: () => {
      // Invalidate related queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ["allocations", userId] });
      queryClient.invalidateQueries({ queryKey: ["strategies"] });
      queryClient.invalidateQueries({ queryKey: ["portfolio", userId] });
    },
  });

  // ============================================================================
  // Return
  // ============================================================================

  return {
    allocations: allocationsQuery.data?.allocations ?? [],
    isLoading: allocationsQuery.isLoading,
    isFetching: allocationsQuery.isFetching,

    allocate: async (params: AllocateParams) => {
      await allocateMutation.mutateAsync(params);
    },

    deallocate: async (params: DeallocateParams) => {
      await deallocateMutation.mutateAsync(params);
    },

    isAllocating: allocateMutation.isPending,
    isDeallocating: deallocateMutation.isPending,

    error: (allocateMutation.error || deallocateMutation.error) as Error | null,

    refetch: allocationsQuery.refetch,
  };
}
